import { JsonSettingsDao } from '@/dao';
import { JsonSettings } from '@/objects/generated';
import type { JsonSettingsType } from '@/objects/generated/enums/JsonSettingsType';
import { defineStore } from 'pinia';
import { ContestantImportSettings } from './ContestantImportSettings';
import { TrackingSettings } from '@/components/views/Tracking/TrackingSettings';
import { PinnedViewsSettings } from '../pinnedviews/pinnedViewsSettings';
import { GridStateSettings } from './GridStateSettings';
import { DismissedMessagesSettings } from './DismissedMessagesSettings';
import { ProductionSettings } from '@/components/views/Production/ProductionSettings';
import { MatrixSettings } from './MatrixSettings';
import type { SettingsBase } from './SettingsBase';
import { SprintSettings } from './SprintSettings';
import { SpeakerSettings } from './SpeakerSettings';
import { EventJsonSettings } from './EventJsonSettings';

/**
 * This one does not have an internal state... Do we
 */
export const useStoredSettings = defineStore('storedsettings', () => {
    // Wee need to cache the original object from the backend so we keep the ID and EntityState since we in the frontend
    // only work with the .Data part of the object and not the whole object
    const map = new Map<string, JsonSettings>();

    // We need a map of the Type and the JsonSettingsType so we can get the settings from the server
    // Here an alternative is to use naming conventions and always have the Type + 'Settings' as the JsonSettingsType
    const typeMap = new Map<string, JsonSettingsType>();
    typeMap.set(ProductionSettings.NAME_OF_CLASS, 'Production');
    typeMap.set(ContestantImportSettings.NAME_OF_CLASS, 'ContestantImport');
    typeMap.set(TrackingSettings.NAME_OF_CLASS, 'TrackingController');
    typeMap.set(PinnedViewsSettings.NAME_OF_CLASS, 'PinnedViews');
    typeMap.set(GridStateSettings.NAME_OF_CLASS, 'GridState');
    typeMap.set(DismissedMessagesSettings.NAME_OF_CLASS, 'DismissedMessages');
    typeMap.set(MatrixSettings.NAME_OF_CLASS, 'Matrix');
    typeMap.set(SprintSettings.NAME_OF_CLASS, 'Sprint');
    typeMap.set(SpeakerSettings.NAME_OF_CLASS, 'Speaker');
    typeMap.set(EventJsonSettings.NAME_OF_CLASS, 'EventJsonSettings');

    /**
     * This will get the settings from the server and return them as the type specified
     * @param type '
     * @param EventId
     * @param UserId
     * @param OrganizerId
     * @returns
     */
    const GetSettings = async <T extends SettingsBase>(
        type: (new (json?: any) => T) & { NAME_OF_CLASS: string },
        EventId: number | null,
        UserId?: number | null,
        OrganizerId?: number | null,
    ): Promise<T | null> => {
        if (!typeMap.has(type.NAME_OF_CLASS)) {
            throw new Error(`Type ${type.NAME_OF_CLASS} is not supported`);
        }

        const settingType: JsonSettingsType = typeMap.get(type.NAME_OF_CLASS)!;
        const settings = await JsonSettingsDao.GetSettings(settingType, EventId, UserId ?? null, OrganizerId ?? null);

        if (settings == null) {
            return null;
        }

        // We save the settings in a map so we can get them later
        const mapkey = `${settingType}-${EventId ?? null}-${UserId ?? null}-${OrganizerId ?? null}`;
        map.set(mapkey, settings);

        let value: T | null = null;

        try {
            // The double constructor here is so we can have an object instance to call the deserialize function on
            value = new type(new type().deserialize(settings.Data));
        } catch (ex) {
            console.error(ex);
            return null;
        }

        return value;
    };

    /**
     * This converts the settings to a JsonSettings object and saves it to the server
     * If the settings already exist it will update them
     * @param type
     * @param settings
     * @param EventId
     * @param UserId
     * @param OrganizerId
     * @param Expires Will set an expires time. This mean the settings will be unavailable after this time
     * @returns
     */
    const SaveSettings = async <T extends SettingsBase>(
        type: (new (json?: any) => T) & { NAME_OF_CLASS: string },
        settings: T,
        EventId: number | null,
        UserId?: number | null,
        OrganizerId?: number | null,
        Expires?: Date | null,
    ): Promise<number | null> => {
        if (!typeMap.has(type.NAME_OF_CLASS)) {
            throw new Error(`Type ${type.NAME_OF_CLASS} is not supported`);
        }
        const settingType: JsonSettingsType = typeMap.get(type.NAME_OF_CLASS)!;

        const mapkey = `${settingType}-${EventId ?? null}-${UserId ?? null}-${OrganizerId ?? null}`;

        // Get the actual JsonSettings object from the map. This is the one we need to save back to the server
        // If it doesnt exist we create a new one with id = 0 and EntityState = 'Added'
        let jsonSettings = map.get(mapkey);
        if (jsonSettings == undefined) {
            jsonSettings = JsonSettings.FromJsValues({
                Id: 0,
                EntityState: 'Added',
            });
        }

        jsonSettings.EventId = EventId;
        jsonSettings.UserId = UserId ?? null;
        jsonSettings.OrganizerId = OrganizerId ?? null;
        jsonSettings.Type = settingType;
        jsonSettings.Expires = Expires ?? null;
        jsonSettings.Data = settings.serialize();

        jsonSettings.Id = (await JsonSettingsDao.SaveSettings(jsonSettings)) ?? 0;

        // We need to save the settings back to the map so we can get them later
        map.set(mapkey, jsonSettings);

        return jsonSettings.Id;
    };

    return {
        GetSettings,
        SaveSettings,
    };
});
